import { IRefIds } from 'constants/refs';
import { get, post } from 'services/request';
import { BaseModel, IModelOptions } from './BaseModel';
import { AIIllustrationItemStatus, IAIIllustrationItem } from 'types/aiIllustration';

export class AIIllustrationItemsModel extends BaseModel<IAIIllustrationItem, null> {
  jobId?: number;
  private pollTimer: number | null = null;
  private isPolling = false;
  private shadowProcessingAlternatives: Map<number, number[]> = new Map();
  private approvingAlternatives: Map<number, number> = new Map(); // itemId -> alternativeNumber being approved

  constructor(options: IModelOptions & { jobId?: number }) {
    super(options);
    this.jobId = options.jobId;
    this.ref = IRefIds.aiIllustration;
    this.gridName = 'AIIllustrationItems';
    this.getGridColumns();
  }

  setJobId(jobId: number) {
    this.jobId = jobId;
    this.gridData = [];
    this.getGrid().then(() => {
      // Start polling if there are items being processed
      this.startPollingIfNeeded();
    });
  }

  async getGrid() {
    if (!this.jobId) return;

    await super.getGrid();
    this.gridData = await get<IAIIllustrationItem[]>(`/ai-illustration/jobs/${this.jobId}/items`);

    this.loadingGrid = false;
    this.render();
  }

  async refresh() {
    await this.getGrid();
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async loadItemData(itemId: number) {
    return {};
  }

  async getGridRow(itemId: number) {
    // Items don't have individual endpoints, find from grid data
    const item = this.gridData?.find((i: IAIIllustrationItem) => i.id === itemId);
    if (!item) return { id: itemId };

    // Merge shadow processing_alternatives if exists
    const shadowAlternatives = this.shadowProcessingAlternatives.get(itemId);
    if (shadowAlternatives) {
      // Return item with shadow processing_alternatives
      return {
        ...item,
        processing_alternatives: shadowAlternatives,
      };
    }

    return item;
  }

  /**
   * Remix/edit an alternative image with additional prompt instructions
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - 1 or 2
   * @param additionalPrompt - User's modification instructions
   * @param useEnhancedBase - true = use enhanced image, false = use original PNG
   */
  async remixItem(
    itemId: number,
    alternativeNumber: number,
    additionalPrompt: string,
    useEnhancedBase: boolean,
    flattenBgColor: string | null = null,
  ): Promise<void> {
    // Optimistic update: mark alternative as processing immediately
    this.updateItemProcessingState(itemId, alternativeNumber, true);
    this.render();

    try {
      await post(`/ai-illustration/items/${itemId}/remix`, {
        alternative_number: alternativeNumber,
        additional_prompt: additionalPrompt,
        use_enhanced_base: useEnhancedBase,
        ...(flattenBgColor ? { flatten_bg_color: flattenBgColor } : {}),
      });
      // Start polling to get updates
      this.startPollingIfNeeded();
    } catch (error) {
      // Revert optimistic update on error
      this.updateItemProcessingState(itemId, alternativeNumber, false);
      this.render();
      throw error;
    }
  }

  /**
   * Check if a specific alternative is being approved
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - 1 or 2
   */
  isApprovingAlternative(itemId: number, alternativeNumber: number): boolean {
    return this.approvingAlternatives.get(itemId) === alternativeNumber;
  }

  /**
   * Check if any alternative for an item is being approved
   * @param itemId - The AIIllustrationItem ID
   */
  isApprovingItem(itemId: number): boolean {
    return this.approvingAlternatives.has(itemId);
  }

  /**
   * Approve a specific alternative as the final enhanced illustration
   * This is a synchronous operation - no polling needed
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - 1 or 2
   */
  async approveItem(itemId: number, alternativeNumber: number): Promise<void> {
    // Set approving state for loading indicator
    this.approvingAlternatives.set(itemId, alternativeNumber);
    this.updateGridRow(itemId, 'update');
    this.render();

    try {
      await post(`/ai-illustration/items/${itemId}/approve`, {
        alternative_number: alternativeNumber,
      });

      // Clear approving state
      this.approvingAlternatives.delete(itemId);

      // Update item in gridData to reflect the approval
      this.updateItemAfterApproval(itemId, alternativeNumber);

      // Update the grid row
      this.updateGridRow(itemId, 'update');
      this.render();
    } catch (error) {
      // Clear approving state on error
      this.approvingAlternatives.delete(itemId);
      this.updateGridRow(itemId, 'update');
      this.render();
      throw error;
    }
  }

  /**
   * Update item data after successful approval
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - The alternative that was approved (1 or 2)
   */
  private updateItemAfterApproval(itemId: number, alternativeNumber: number): void {
    const item = this.gridData?.find((i: IAIIllustrationItem) => i.id === itemId);
    if (!item) return;

    // Update status to DONE
    item.status = AIIllustrationItemStatus.DONE;

    // Update enhanced_results with approved_alternative
    if (item.enhanced_results) {
      item.enhanced_results.approved_alternative = alternativeNumber;
      item.enhanced_results.approved_at = new Date().toISOString();
    }
  }

  /**
   * Redo the enhancement for a specific alternative
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - 1 or 2
   */
  async redoItem(itemId: number, alternativeNumber: number): Promise<void> {
    // Optimistic update: mark alternative as processing immediately
    this.updateItemProcessingState(itemId, alternativeNumber, true);
    this.render();

    try {
      await post(`/ai-illustration/items/${itemId}/redo`, {
        alternative_number: alternativeNumber,
      });
      // Start polling to get updates
      this.startPollingIfNeeded();
    } catch (error) {
      // Revert optimistic update on error
      this.updateItemProcessingState(itemId, alternativeNumber, false);
      this.render();
      throw error;
    }
  }

  /**
   * Fix faces on an alternative by swapping black line art from the original.
   * Synchronous Imagick operation — busts image cache and refreshes the row.
   */
  async fixFaces(
    itemId: number,
    alternativeNumber: number,
    maskBase64: string,
    deltaX: number = 0,
    deltaY: number = 0,
  ): Promise<void> {
    await post(`/ai-illustration/items/${itemId}/fix-faces`, {
      alternative_number: alternativeNumber,
      mask_base64: maskBase64,
      delta_x: deltaX,
      delta_y: deltaY,
    });
    this.bustImageCache(itemId, alternativeNumber);
  }

  /**
   * Bust browser cache for an alternative's image URL and refresh the row.
   */
  private bustImageCache(itemId: number, alternativeNumber: number): void {
    const item = this.gridData?.find((i: IAIIllustrationItem) => i.id === itemId);
    if (!item) return;

    const key = `alternative_${alternativeNumber}` as keyof IAIIllustrationItem;
    const url = item[key] as string | null;
    if (url) {
      // Replace or append cache-busting param
      const busted = url.replace(/[?&]v=\d+/, '') + '?v=' + Date.now();
      (item as any)[key] = busted;
    }
    this.updateGridRow(itemId, 'update');
  }

  /**
   * Compare two processing_alternatives arrays to detect changes
   * @param oldArray - Previous processing_alternatives array
   * @param newArray - New processing_alternatives array
   * @returns true if arrays differ, false if identical
   */
  private hasProcessingAlternativesChanged(oldArray: number[] | undefined, newArray: number[] | undefined): boolean {
    // Handle null/undefined cases
    const oldArr = oldArray || [];
    const newArr = newArray || [];

    // Different lengths = changed
    if (oldArr.length !== newArr.length) return true;

    // Compare contents
    return !oldArr.every((val) => newArr.includes(val));
  }

  /**
   * Update the processing state of a specific alternative in the grid data
   * Uses shadow copy to track optimistic updates without mutating gridData
   * @param itemId - The AIIllustrationItem ID
   * @param alternativeNumber - 1 or 2
   * @param isProcessing - true to add to processing array, false to remove
   */
  private updateItemProcessingState(itemId: number, alternativeNumber: number, isProcessing: boolean): void {
    const item = this.gridData?.find((i: IAIIllustrationItem) => i.id === itemId);
    if (!item) return;

    // Get current shadow or use item's processing_alternatives as base
    const currentShadow = this.shadowProcessingAlternatives.get(itemId) || item.processing_alternatives || [];

    let newShadow: number[];
    if (isProcessing) {
      // Add to processing array if not already there
      if (!currentShadow.includes(alternativeNumber)) {
        newShadow = [...currentShadow, alternativeNumber];
      } else {
        newShadow = currentShadow;
      }
    } else {
      // Remove from processing array
      newShadow = currentShadow.filter((n: number) => n !== alternativeNumber);
    }

    // Update shadow copy
    if (newShadow.length > 0) {
      this.shadowProcessingAlternatives.set(itemId, newShadow);
    } else {
      // Clear shadow if no processing alternatives
      this.shadowProcessingAlternatives.delete(itemId);
    }

    // Use updateGridRow to properly update the row
    this.updateGridRow(itemId, 'update');
  }

  /**
   * Start polling if there are any items with processing_alternatives
   * Checks both gridData and shadow copy for processing items
   */
  public startPollingIfNeeded(): void {
    // Check gridData for items with processing_alternatives from backend
    const hasProcessingInGridData = this.gridData?.some(
      (item: IAIIllustrationItem) => item.processing_alternatives && item.processing_alternatives.length > 0,
    );

    // Check shadow copy for optimistic updates
    const hasProcessingInShadow = this.shadowProcessingAlternatives.size > 0;

    const hasProcessingItems = hasProcessingInGridData || hasProcessingInShadow;

    if (hasProcessingItems && !this.isPolling) {
      this.startPolling();
    }
  }

  /**
   * Start the polling timer
   */
  private startPolling(): void {
    this.isPolling = true;
    this.poll();
  }

  /**
   * Poll for updates to items with processing_alternatives
   */
  private async poll(): Promise<void> {
    if (!this.isPolling || !this.jobId) return;

    try {
      const freshData = await get<IAIIllustrationItem[]>(`/ai-illustration/jobs/${this.jobId}/items`);

      // Clear all shadow data - backend is always source of truth
      this.shadowProcessingAlternatives.clear();

      // Track items that need updates
      const itemsToUpdate: number[] = [];

      // Check each item for processing_alternatives changes
      freshData.forEach((freshItem) => {
        const existingItem = this.gridData?.find((i) => i.id === freshItem.id);
        if (!existingItem) return;

        // Check if processing_alternatives changed (backend is source of truth)
        if (
          this.hasProcessingAlternativesChanged(existingItem.processing_alternatives, freshItem.processing_alternatives)
        ) {
          itemsToUpdate.push(freshItem.id);
        }
      });

      // Update each changed item
      for (const itemId of itemsToUpdate) {
        const freshItem = freshData.find((i) => i.id === itemId);
        if (!freshItem) continue;

        // Update the item in gridData - backend is source of truth
        const existingItem = this.gridData?.find((i) => i.id === itemId);
        if (existingItem) {
          Object.assign(existingItem, freshItem);

          // Use base model's update mechanism
          this.updateGridRow(itemId, 'update');
        }
      }

      // Check if we should stop polling
      // Need to check both backend data and shadow copy
      const stillHasProcessingInBackend = freshData.some(
        (item) => item.processing_alternatives && item.processing_alternatives.length > 0,
      );
      const stillHasProcessingInShadow = this.shadowProcessingAlternatives.size > 0;

      if (!stillHasProcessingInBackend && !stillHasProcessingInShadow) {
        this.stopPolling();
        return;
      }
    } catch (e) {
      // Ignore transient network errors and keep polling
    }

    // Schedule next poll in 10 seconds
    this.pollTimer = window.setTimeout(() => this.poll(), 10000);
  }

  /**
   * Stop polling and clear the timer
   */
  public stopPolling(): void {
    if (this.isPolling) {
      this.isPolling = false;
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }
}
